import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useRef, useState } from "react";
import { preferredScrollBehavior } from "@/lib/scroll-behavior";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface UseSectionTrackingReturn {
  /** Currently active section ID */
  activeSection: string;
  /** Scroll to a section by ID with smooth scrolling */
  scrollToSection: (id: string) => void;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Tracks which section is currently active based on scroll position.
 *
 * Features:
 * - Updates active section as user scrolls
 * - Handles programmatic scrolling with lock period to prevent jitter
 * - Uses RAF for performance
 * - Handles resize events
 * - Reads each section's CSS `scroll-margin-top` as its activation line, so the offset
 *   always matches where `scrollIntoView` lands
 *
 * `sectionIds` must be referentially stable, or the listeners re-subscribe every render.
 */
export function useSectionTracking(sectionIds) {
  const $ = _c(6);
  const [activeSection, setActiveSection] = useState(sectionIds[0] ?? "");
  const programmaticTargetIdRef = useRef(null);
  const programmaticLockUntilRef = useRef(0);
  let t0;
  let t1;
  if ($[0] !== sectionIds) {
    t0 = () => {
      if (sectionIds.length === 0) {
        return;
      }
      let rafId = null;
      const updateActiveSection = () => {
        const targetId = programmaticTargetIdRef.current;
        if (targetId) {
          const now = typeof performance !== "undefined" ? performance.now() : Date.now();
          const targetEl = document.getElementById(targetId);
          if (!targetEl || now > programmaticLockUntilRef.current) {
            programmaticTargetIdRef.current = null;
          } else {
            const {
              top
            } = targetEl.getBoundingClientRect();
            const isLanded = Math.abs(top - getScrollMargin(targetEl)) <= 2;
            if (!isLanded) {
              setActiveSection(prev => prev === targetId ? prev : targetId);
              return;
            }
            programmaticTargetIdRef.current = null;
          }
        }
        const sectionEls = sectionIds.map(_temp).filter(_temp2);
        let nextActive = sectionEls[0]?.id ?? sectionIds[0] ?? "";
        for (const el_0 of sectionEls) {
          const {
            top: top_0
          } = el_0.getBoundingClientRect();
          if (top_0 - getScrollMargin(el_0) <= 1) {
            nextActive = el_0.id;
          } else {
            break;
          }
        }
        setActiveSection(prev_0 => prev_0 === nextActive ? prev_0 : nextActive);
      };
      const scheduleUpdate = () => {
        if (rafId !== null) {
          return;
        }
        rafId = window.requestAnimationFrame(() => {
          rafId = null;
          updateActiveSection();
        });
      };
      window.addEventListener("scroll", scheduleUpdate, {
        passive: true
      });
      window.addEventListener("resize", scheduleUpdate, {
        passive: true
      });
      scheduleUpdate();
      return () => {
        window.removeEventListener("scroll", scheduleUpdate);
        window.removeEventListener("resize", scheduleUpdate);
        if (rafId !== null) {
          window.cancelAnimationFrame(rafId);
        }
      };
    };
    t1 = [sectionIds];
    $[0] = sectionIds;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  useEffect(t0, t1);
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = id_0 => {
      const element = document.getElementById(id_0);
      if (!element) {
        return;
      }
      setActiveSection(id_0);
      programmaticTargetIdRef.current = id_0;
      const now_0 = typeof performance !== "undefined" ? performance.now() : Date.now();
      programmaticLockUntilRef.current = now_0 + 1500;
      element.scrollIntoView({
        behavior: preferredScrollBehavior(),
        block: "start"
      });
    };
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const scrollToSection = t2;
  let t3;
  if ($[4] !== activeSection) {
    t3 = {
      activeSection,
      scrollToSection
    };
    $[4] = activeSection;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  return t3;
}
function _temp2(el) {
  return el instanceof HTMLElement;
}
function _temp(id) {
  return document.getElementById(id);
}
function getScrollMargin(el: HTMLElement): number {
  return Number.parseFloat(getComputedStyle(el).scrollMarginTop) || 0;
}